import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./utils";

type AdminButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "soft" | "plain";
type AdminButtonSize = "sm" | "md" | "icon" | "auto";

interface AdminButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AdminButtonVariant;
  size?: AdminButtonSize;
  icon?: string;
  children?: ReactNode;
}

const variantClass: Record<AdminButtonVariant, string> = {
  primary: "text-white border-transparent hover:opacity-90",
  secondary: "text-slate-600 bg-white border-slate-200 hover:bg-slate-50",
  ghost: "text-slate-500 bg-transparent border-transparent hover:bg-slate-100 hover:text-slate-800",
  danger: "text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100",
  soft: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100",
  plain: "border-transparent bg-transparent",
};

const sizeClass: Record<AdminButtonSize, string> = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-8 px-3 text-xs",
  icon: "h-7 w-7 p-0 text-xs",
  auto: "",
};

const AdminButton = ({
  variant = "secondary",
  size = "md",
  icon,
  children,
  className,
  style,
  type = "button",
  ...props
}: AdminButtonProps) => {
  const primaryStyle =
    variant === "primary"
      ? { background: "linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)", ...style }
      : style;

  return (
    <button
      type={type}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-lg border font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      style={primaryStyle}
      {...props}
    >
      {icon && <i className={`${icon} text-xs`} />}
      {children}
    </button>
  );
};

export default AdminButton;
